import { queryKeys } from '@query/keys'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { AxiosResponseHeaders } from 'axios'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  ErrorCode,
  isClientTestCooldownError,
  resolveApiErrorContent,
} from '@app/routing/RouterToaster'

import { useNavigate } from '@lib/react-router-dom'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useModal } from '@context/ModalsContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'

import { isSumsubSupported } from '@utils/AccountAccess/accountKYCStatuses'
import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { CommonPaths } from '@utils/path'

import { AccountDetailedDto, isAccountDormantUpdateNeeded } from '@model/AccountDetailedDto'
import { isAccountActivatedStatus } from '@model/AccountDetailedStatus'
import { ClientTestDto } from '@model/ClientTestDto'
import { DocumentCategoryType } from '@model/DocumentCategories'
import { DocumentCategoryOptionType } from '@model/DocumentCategoryOptionType'

import { useClientTestSubmission } from '@hooks/useClientTestSubmission'

import { Loading } from '@shared/components/Loading/Loading'
import { AlreadyUploadedModal } from '@shared/modals/AlreadyUploaded.modal'
import { getCleanedAnswers } from '@shared/templates/Test/TestFields/TestFields'

import {
  YearlyKycUpdateDetailsForm,
  YearlyKycUpdateDetailsFormValues,
} from './YearlyKycUpdateDetailsForm/YearlyKycUpdateDetailsForm'

export const useYearlyKycUpdateDetails = () => {
  const apiClient = useApiClient(ClientApiClient)
  const locale = useSessionLanguage()

  const { data = [], isLoading } = useQuery({
    queryKey: queryKeys.profile.yearlyKycUpdate(locale),
    queryFn: () =>
      Promise.all([
        apiClient.getCountries({
          displayAllCountries: true,
          showHiddenCountries: true,
        }),
        apiClient.getAccountKycTest(locale),
        apiClient.getTaxUnavailableReasons(locale),
      ]),
  })
  const [countries = [], accountKycTest, tinUnavailableReasons = []] = data

  return { countries, accountKycTest, tinUnavailableReasons, isLoading }
}

export const useYearlyKycUpdateDetailsMutate = (
  productRegistrationRequirement?: boolean,
  options?: { onSuccess?: () => void }
) => {
  const apiClient = useApiClient(ClientApiClient)
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const { performSubmission, submitTest, uploadDocuments, isLoading } = useClientTestSubmission()
  const [isSuccess, setSuccess] = useState(false)
  const mutate = async (
    account: AccountDetailedDto | undefined,
    accountKycTest: ClientTestDto | undefined,
    values: YearlyKycUpdateDetailsFormValues,
    openAlreadyUploadedModal: () => void
  ) => {
    await performSubmission(
      async () => {
        const { selectedAnswers: cleanSelected, freeAnswers: cleanFree } = getCleanedAnswers(
          values.selectedAnswers,
          values.freeAnswers,
          accountKycTest?.sections || []
        )

        const cleanValues = { ...values, selectedAnswers: cleanSelected, freeAnswers: cleanFree }

        const isKYCNeeded =
          !isAccountDormantUpdateNeeded(account) &&
          (account?.isKYCUpdateNeeded || productRegistrationRequirement)
        const isDormantNeeded = isAccountDormantUpdateNeeded(account)

        let responseHeaders

        if (isKYCNeeded) {
          await submitTest(cleanValues, {
            clientId: values.clientId,
            category: values?.accountKycTest?.category,
            testId: accountKycTest?.id,
          })

          const response = await apiClient.updateAccountKycDetailsResponse({
            clientId: values.clientId,
            phoneNumber: values.phoneNumber,
            phoneCountryCode: values.countryCode,
            state: values.state,
            city: values.city,
            street: values.street,
            streetHouseNo: values.streetHouseNo,
            streetHouseNoNotAvailable: !values.streetHouseNo,
            zipCode: values.zipCode,
            taxIdentificationNumber: values.taxIdentificationNumber,
            kycUpdateTestId: accountKycTest?.id || '',
            taxIdAvailable: values.taxIdAvailable,
            taxIdUnavailableReasonId: values.taxIdUnavailableReasonId,
          })
          await queryClient.invalidateQueries({ queryKey: ['document'] })
          await queryClient.invalidateQueries({ queryKey: ['account'] })
          responseHeaders = response.headers

          await uploadDocuments(values, {
            categoryId: DocumentCategoryType.Additional,
            typeId: DocumentCategoryOptionType.Other,
            ticketIdOrTestId: getTestId(responseHeaders),
            isTicketId: true,
          })
        }

        if (isDormantNeeded) {
          const response = await apiClient.updateAccountDormantDetailsResponse({
            clientId: values.clientId,
            phoneNumber: values.phoneNumber,
            phoneCountryCode: values.countryCode,
            state: values.state,
            city: values.city,
            street: values.street,
            zipCode: values.zipCode,
            taxIdentificationNumber: values.taxIdentificationNumber,
            taxIdUnavailableReasonId: values.taxIdUnavailableReasonId || null,
            taxIdAvailable: values.taxIdAvailable,
          })
          await queryClient.invalidateQueries({ queryKey: ['account'] })
          responseHeaders = response.headers

          if (!shouldRedirectToSumsub(account)) {
            await uploadDocuments(values, {
              categoryId: DocumentCategoryType.Address,
              typeId: DocumentCategoryOptionType.Other,
              ticketIdOrTestId: getTestId(responseHeaders),
              isTicketId: true,
            })
          }
        }

        if (options?.onSuccess) {
          options.onSuccess()
          return
        }

        setSuccess(true)
      },
      (error) => {
        const code = error?.response?.response?.data?.code
        const errorDisplayContent = resolveApiErrorContent(code as ErrorCode, t)
        if (!errorDisplayContent.title || !isClientTestCooldownError(code)) {
          openAlreadyUploadedModal()
        }
      }
    )
  }

  return { mutate, isLoading, isSuccess }
}

interface YearlyKycUpdateDetailsPageProps {
  accountPassed?: AccountDetailedDto
}

export const YearlyKycUpdateDetailsPage: React.FC<YearlyKycUpdateDetailsPageProps> = ({
  accountPassed,
}) => {
  const { account } = useAccountReadContext()
  const navigate = useNavigate()

  const { countries, accountKycTest, tinUnavailableReasons, isLoading } =
    useYearlyKycUpdateDetails()

  const mutation = useYearlyKycUpdateDetailsMutate(undefined, {
    onSuccess: () => navigate(CommonPaths.KycUpdateSuccess, { replace: true }),
  })

  const handleSubmitForm = async (values: YearlyKycUpdateDetailsFormValues) => {
    await mutation.mutate(account, accountKycTest, values, alreadyUploadedModal.open)
  }

  const alreadyUploadedModal = useModal(AlreadyUploadedModal)

  return (
    <Loading showLoadingIcon isLoading={isLoading || mutation.isLoading}>
      {!mutation.isSuccess && !mutation.isLoading && (
        <YearlyKycUpdateDetailsForm
          account={accountPassed || account}
          countries={countries}
          accountKycTest={accountKycTest}
          onSubmit={handleSubmitForm}
          tinUnavailableReasons={tinUnavailableReasons}
        />
      )}
    </Loading>
  )
}

const getTestId = (
  headers:
    | AxiosResponseHeaders
    | Partial<Record<string, string> & { 'set-cookie'?: string[] | undefined }>
) => {
  const parts = headers.location?.split('/')
  if (parts) {
    return parts[parts.length - 1]
  }
  return ''
}

export const isKycUpdatePending = (accountDetailed?: AccountDetailedDto): boolean => {
  if (!accountDetailed) {
    return false
  }

  return accountDetailed?.isKYCUpdateNeeded && !accountDetailed?.isKYCUpdateRequired
}

export const shouldRedirectToSumsub = (account: AccountDetailedDto | undefined): boolean => {
  return !!(account && isSumsubSupported(account) && !isAccountActivatedStatus(account))
}
